import { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { updateFilter } from 'redux/slices/FilterBuilder'
import { trackActions } from 'containers/Helper'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import {
  YearOfBirthFilterOptions,
  generateDisplayNameandValues,
  generateHelperText,
  initialYearFilterError,
  initialYearFilterValue,
} from 'components/Reporting/Overview/FilterBuilder/Filters/YearsOfBirth/Helper'
import EditIcon from 'components/Reporting/Overview/FilterBuilder/Filters/YearsOfBirth/EditIcon'
import YOBFilterDialog from 'components/Reporting/Overview/FilterBuilder/Filters/YearsOfBirth/DialogComponent'
import { FilterItem } from 'types/FilterBuilder'

type Props = {
  item: FilterItem
  index: number
  isAutoFocusEnabled?: boolean
}

const YearsOfBirth = (props: Props) => {
  const { t } = useTranslation()
  const {
    index,
    item: { value },
  } = props
  const [modalState, setModalState] = useState(value ? false : true)
  const [yearFilterValue, setYearFilterValue] = useState(
    initialYearFilterValue(value),
  )
  const [yearFilterError, setYearFilterError] = useState(initialYearFilterError)
  const theme: AdminUITheme = useTheme()
  const dispatch = useAppDispatch()
  const { features: { features: currentFeatures = [] } = {} } = useAppSelector(
    (state) => state,
  )

  const handleCancel = () => setModalState(false)

  const handleYearFilterSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selection =
      e && e.target && e.target.value ? e.target.value : 'equals'
    setYearFilterValue({
      ...yearFilterValue,
      ...{ label: selection, to: 0, from: 0, year: 0, values: [] },
    })
    setYearFilterError({
      ...yearFilterError,
      ...{
        label: selection,
        to_error: '',
        from_error: '',
        year_error: '',
        state: false,
      },
    })
  }
  const handleOpenModal = () => {
    setModalState(true)
    setYearFilterError(initialYearFilterError)
    setYearFilterValue(initialYearFilterValue(value))
  }

  const handleFilterByYearOfBirth = () => {
    const errorObj = generateHelperText(
      initialYearFilterError,
      yearFilterValue,
      t,
    )
    if (errorObj.state) {
      setYearFilterError(errorObj)
    } else {
      const newObj = generateDisplayNameandValues(yearFilterValue, t)
      dispatch(
        updateFilter({
          item: { value: newObj.values ? newObj.values : [] },
          index,
        }),
      )
      setYearFilterError(initialYearFilterError)
      setYearFilterValue(initialYearFilterValue(value))
      setModalState(false)
    }
  }
  const handleYearEntry = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    const newObj: any = {}
    const regex = /^[0-9\b]+$/
    newObj[key] =
      e && e.target && e.target.value ? parseInt(e.target.value) : ''
    if (newObj[key] === '' || regex.test(newObj[key])) {
      setYearFilterValue({ ...yearFilterValue, ...newObj })
    }
  }

  useEffect(() => {
    trackActions('Clicked on Year of birth filter option', {
      featureName: 'Overview dashboard filters',
      sectionName: 'Overview (Home) Dashboard page',
      ...currentFeatures?.['segment_analytics_admin']?.enabled,
    })
  }, [])

  const displayValue = value?.length
    ? value?.[0]?.displayName?.replaceAll('_', ' ')
    : ''

  return (
    <>
      <YOBFilterDialog
        {...{
          modalState,
          YearOfBirthFilterOptions,
          handleYearFilterSelection,
          yearFilterValue,
          yearFilterError,
          handleYearEntry,
          handleCancel,
          handleFilterByYearOfBirth,
        }}
      />
      <FormControl sx={{ width: '100%' }}>
        <Autocomplete
          sx={{
            ...{
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                pr: 3,
              },
            },
            ...theme.GenericTheme.dynamicFilterWrapperWidth,
          }}
          options={[]}
          value={displayValue}
          data-testid="yob-filter"
          id="yob-filter"
          popupIcon={<EditIcon {...{ handleOpenModal }} />}
          size="small"
          readOnly
          renderInput={(params) => (
            <TextField {...params} label={t(`filters.yob.label`)} />
          )}
        />
      </FormControl>
    </>
  )
}

export default YearsOfBirth
